import { useCallback, useEffect, useRef, useState } from "react";
import { createEventSource, type EventSourceClient } from "eventsource-client";

import { firebaseAuth } from "#app/firebase";

const BRIDGE_CONNECTION_TIMEOUT = 5000; // 5 seconds to understand if deployed bridge has requested endpoint

interface UseSSEQueryOptions {
    endpoint: string;
    enabled: boolean;
    params: unknown;
}

export const useSSEQuery = <T = any>({
    endpoint,
    enabled,
    params,
}: UseSSEQueryOptions) => {
    const clientRef = useRef<EventSourceClient | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [data, setData] = useState<T | null | undefined>(undefined);
    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const paramsString = JSON.stringify(params);

    const setupSSE = useCallback(async (): Promise<void> => {
        setIsLoading(true);
        setData(undefined);
        setError(null);

        timeoutRef.current = setTimeout(() => {
            if (clientRef.current) clientRef.current.close();
            if (abortControllerRef.current) abortControllerRef.current.abort();
            setError(
                new Error(
                    `Aborting after ${BRIDGE_CONNECTION_TIMEOUT / 1000} seconds`,
                ),
            );
            setData(null);
            setIsLoading(false);
        }, BRIDGE_CONNECTION_TIMEOUT);

        const token = await firebaseAuth.currentUser?.getIdToken(true);
        if (!token) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setError(new Error("No Firebase token available"));
            setIsLoading(false);
            return;
        }

        abortControllerRef.current = new AbortController();

        const client = createEventSource({
            url: endpoint,
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
            body: paramsString,
            onConnect: () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            },
            onScheduleReconnect: () => {
                setError(new Error("Bridge connection failed"));
                setData(null);
                setTimeout(() => {
                    clientRef.current?.close();
                }, 0);
            },
            onDisconnect: () => {
                setError(new Error("Bridge connection failed"));
                setData(null);
                setIsLoading(false);
            },
        });

        clientRef.current = client;
        setIsLoading(false);

        // Process streaming events - only catch stream-specific errors
        try {
            for await (const event of client) {
                if (abortControllerRef.current.signal.aborted) break;

                try {
                    const eventData = JSON.parse(event.data) as T;
                    setData(eventData);
                } catch {
                    // Skip malformed events, continue processing
                }
            }
        } catch (streamError) {
            // Only catch SSE stream errors
            setError(streamError as Error);
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint, paramsString]);

    useEffect(() => {
        if (!enabled) return;

        void setupSSE();

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (abortControllerRef.current) abortControllerRef.current.abort();
            if (clientRef.current) clientRef.current.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, endpoint, paramsString]);

    return {
        data,
        error,
        isLoading,
    };
};
